import { useForm } from "@tellescope/forms";
import {
  TellescopeModelFromName,
  useAuth,
  useTellescopeMutation,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  PageTabs,
  PageTabsContent,
  PageTabsList,
  PageTabsTrigger,
  withKey,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { AppointmentTerm, BookingRestrictions } from "@tellescope/types-models";
import { BookingPageFormDialogContentsFormConfig } from "./types";
import { convertStringToCollectReason, convertStringToEmailFieldBehavior } from "../../../utils";
import { InfoSection } from "./InfoSection";
import { RestrictionsSection } from "./RestrictionsSection";
import { CustomizationSection } from "./CustomizationSection";
import { AnalyticsSection } from "./AnalyticsSection";
import { deepEqual } from "@tellescope/utils";

const DEFAULT_BOOKING_RESTRICTION: BookingRestrictions["restrictions"] = {
  careTeam: false,
  state: false,
  hoursBefore: undefined,
  hoursAfter: undefined,
  shouldOpenJoinLink: false,
};

export interface BookingPageFormDialogContentsProps {
  bookingPage?: TellescopeModelFromName<"appointment_booking_pages">;
  bookingPageId?: string;
  onSave?: () => void;
  onCancel?: () => void;
  shouldDuplicate?: boolean;
}

export const BookingPageFormDialogContents = withKey(
  ({
    bookingPage: providedBookingPage,
    bookingPageId,
    onSave,
    onCancel,
    shouldDuplicate,
  }: BookingPageFormDialogContentsProps) => {
    const { t } = useTranslation("calendar");
    const { selectedOrganization } = useAuth();
    const bookingPageQuery = useTellescopeQuery({
      model: "appointment_booking_pages",
      method: "getOne",
      request: bookingPageId ?? "",
      enabled: !providedBookingPage && !!bookingPageId,
    });
    const bookingPage = providedBookingPage ?? bookingPageQuery.data;
    const [selectedTab, setSelectedTab] = useState("info");

    const createBookingPageMutation = useTellescopeMutation({
      model: "appointment_booking_pages",
      method: "createOne",
    });
    const updateBookingPageMutation = useTellescopeMutation({
      model: "appointment_booking_pages",
      method: "updateOne",
    });

    const [newTerms, setNewTerms] = useState<AppointmentTerm[]>();
    const resolvedTerms = useMemo(() => newTerms ?? bookingPage?.terms ?? [], [bookingPage?.terms, newTerms]);

    const [templateRestrictions, setTemplateRestrictions] =
      useState<Record<string, BookingRestrictions["restrictions"]>>();
    const bookingPageDerivedRestrictions = useMemo(
      () =>
        bookingPage?.restrictionsByTemplate?.reduce<Record<string, BookingRestrictions["restrictions"]>>(
          (acc, curr) => {
            acc[curr.templateId] = curr.restrictions;
            return acc;
          },
          {}
        ),
      [bookingPage?.restrictionsByTemplate]
    );
    const resolvedTemplateRestrictions = templateRestrictions ?? bookingPageDerivedRestrictions;
    const form = useForm<BookingPageFormDialogContentsFormConfig>({
      fields: {
        title: {
          type: "text",
          initialValue:
            shouldDuplicate && bookingPage?.title
              ? t("{{originalTitle}} (copy)", { originalTitle: bookingPage?.title })
              : bookingPage?.title,
          required: true,
        },
        hiddenFromPortal: {
          type: "checkbox",
          initialValue: bookingPage?.hiddenFromPortal,
        },
        archivedAt: {
          type: "checkbox",
          initialValue: !!bookingPage?.archivedAt,
        },
        includeRelatedContactTypes: {
          type: "multiselect",
          initialValue: bookingPage?.includeRelatedContactTypes ?? [],
        },
        organizationIds: {
          type: "multiselect", // The selected organization's own `organizationIds` array
          initialValue: bookingPage?.organizationIds ?? selectedOrganization?.organizationIds ?? [],
        },
        calendarEventTemplateIds: {
          type: "multiselect",
          initialValue: bookingPage?.calendarEventTemplateIds ?? [],
          required: true,
        },
        locationIds: {
          type: "multiselect",
          initialValue: bookingPage?.locationIds ?? [],
          required: true,
        },
        requireLocationSelection: {
          type: "checkbox",
          initialValue: bookingPage?.requireLocationSelection,
        },
        collectReason: {
          type: "select",
          initialValue: bookingPage?.collectReason || "Optional",
        },
        limitedToCareTeam: {
          type: "checkbox",
          initialValue: bookingPage?.limitedToCareTeam,
        },
        limitedByState: {
          type: "checkbox",
          initialValue: bookingPage?.limitedByState,
        },
        hoursBeforeBookingAllowed: {
          type: "number",
          initialValue: bookingPage?.hoursBeforeBookingAllowed || 24,
        },
        limitedByTagsPortal: {
          type: "multiselect",
          initialValue: bookingPage?.limitedByTagsPortal ?? [],
        },
        dontRestrictRescheduleToOriginalHost: {
          type: "checkbox",
          initialValue: bookingPage?.dontRestrictRescheduleToOriginalHost,
        },
        emailFieldBehavior: {
          type: "select",
          initialValue: bookingPage?.emailFieldBehavior || "required",
        },
        topLogo: {
          type: "url",
          initialValue: bookingPage?.topLogo,
        },
        intakeTitle: {
          type: "text",
          initialValue: bookingPage?.intakeTitle,
        },
        calendarTitleText: {
          type: "text",
          initialValue: bookingPage?.calendarTitleText,
        },
        intakeDescription: {
          type: "textarea",
          initialValue: bookingPage?.intakeDescription,
        },
        thankYouRedirectURL: {
          type: "url",
          initialValue: bookingPage?.thankYouRedirectURL,
        },
        thankYouTitle: {
          type: "text",
          initialValue: bookingPage?.thankYouTitle,
        },
        thankYouHeaderImageURL: {
          type: "url",
          initialValue: bookingPage?.thankYouHeaderImageURL,
        },
        thankYouDescription: {
          type: "text",
          initialValue: bookingPage?.thankYouDescription,
        },
        thankYouMainImageURL: {
          type: "url",
          initialValue: bookingPage?.thankYouMainImageURL,
        },
        primaryColor: {
          type: "color",
          initialValue: bookingPage?.primaryColor,
        },
        backgroundColor: {
          type: "color",
          initialValue: bookingPage?.backgroundColor,
        },
        fontURL: {
          type: "url",
          initialValue: bookingPage?.fontURL,
        },
        fontFace: {
          type: "text",
          initialValue: bookingPage?.fontFace,
        },
        appointmentSlotsMaxHeight: {
          type: "number",
          initialValue: bookingPage?.appointmentSlotsMaxHeight,
        },
        language: {
          type: "select",
          initialValue: bookingPage?.language ?? "English",
        },
        publicShowLanguage: {
          type: "checkbox",
          initialValue: bookingPage?.publicShowLanguage,
        },
        gtmTag: {
          type: "text",
          initialValue: bookingPage?.gtmTag,
        },
        ga4measurementId: {
          type: "text",
          initialValue: bookingPage?.ga4measurementId,
        },
      },
      sideEffects: {
        calendarEventTemplateIds: (calendarEventTemplateIds, all) => {
          setTemplateRestrictions((prev) => {
            return calendarEventTemplateIds.reduce<typeof prev>((acc, curr) => {
              const entry = prev?.[curr] ?? DEFAULT_BOOKING_RESTRICTION;
              if (!acc) {
                return {
                  [curr]: entry,
                };
              }
              acc[curr] = entry;
              return acc;
            }, {});
          });

          return {
            ...all,
            calendarEventTemplateIds,
          };
        },
        locationIds: (locationIds, all) => {
          return {
            ...all,
            requireLocationSelection: locationIds.length < 2 ? false : all.requireLocationSelection,
          };
        },
      },
      onSubmit: async (values) => {
        const restrictionsByTemplate = resolvedTemplateRestrictions
          ? Object.entries(resolvedTemplateRestrictions).reduce<BookingRestrictions[]>(
              (acc, [templateId, restrictions]) => {
                acc.push({
                  templateId,
                  restrictions,
                });

                return acc;
              },
              []
            )
          : undefined;
        const calendarEventTemplateIds = values.calendarEventTemplateIds.filter(Boolean);
        const locationIds = values.locationIds.filter(Boolean);
        const terms = resolvedTerms.filter(({ link, title }) => !!link && !!title);

        if (bookingPage && !shouldDuplicate) {
          await updateBookingPageMutation.mutateAsync({
            id: bookingPage.id,
            ...values,
            archivedAt: values.archivedAt ? bookingPage.archivedAt ?? new Date() : undefined,
            terms,
            collectReason: convertStringToCollectReason(values.collectReason),
            emailFieldBehavior: convertStringToEmailFieldBehavior(values.emailFieldBehavior),
            restrictionsByTemplate,
            calendarEventTemplateIds,
            locationIds,
            _options: {
              replaceObjectFields: true,
            },
          });
        } else {
          await createBookingPageMutation.mutateAsync({
            ...values,
            terms,
            archivedAt: values.archivedAt ? new Date() : undefined,
            collectReason: convertStringToCollectReason(values.collectReason),
            emailFieldBehavior: convertStringToEmailFieldBehavior(values.emailFieldBehavior),
            restrictionsByTemplate,
            calendarEventTemplateIds,
            locationIds,
          });
        }
        onSave?.();
      },
    });

    const formIsDirty = useMemo(
      () =>
        bookingPage && !shouldDuplicate
          ? form.isDirty ||
            !deepEqual(resolvedTemplateRestrictions, bookingPageDerivedRestrictions) ||
            !deepEqual(resolvedTerms, bookingPage.terms)
          : true,
      [
        bookingPage,
        bookingPageDerivedRestrictions,
        form.isDirty,
        resolvedTemplateRestrictions,
        resolvedTerms,
        shouldDuplicate,
      ]
    );

    return (
      <DialogContent divided data-testid="booking-page-form-dialog" className="sm:max-w-4xl">
        <DialogHeader className="px-0">
          <div className="space-y-6">
            <h2 className="px-6">
              {bookingPage && !shouldDuplicate
                ? t("Edit Page: {{pageTitle}}", { pageTitle: bookingPage.title })
                : t("Create Booking Page")}
            </h2>
            <PageTabs value={selectedTab} onValueChange={setSelectedTab}>
              <PageTabsList className="h-10 overflow-x-auto px-6">
                <PageTabsTrigger value="info">{t("Info")}</PageTabsTrigger>
                <PageTabsTrigger value="restrictions">{t("Restrictions")}</PageTabsTrigger>
                <PageTabsTrigger value="customization">{t("Customization")}</PageTabsTrigger>
                <PageTabsTrigger value="analytics">{t("Analytics")}</PageTabsTrigger>
              </PageTabsList>
              <form {...form.getFormProps()} className="p-6 overflow-y-auto" style={{ height: "min(80svh, 875px)" }}>
                <PageTabsContent value="info">
                  <InfoSection
                    form={form}
                    terms={resolvedTerms}
                    setTerms={setNewTerms}
                    templateRestrictions={resolvedTemplateRestrictions}
                    setTemplateRestrictions={setTemplateRestrictions}
                  />
                </PageTabsContent>
                <PageTabsContent value="restrictions">
                  <RestrictionsSection form={form} />
                </PageTabsContent>
                <PageTabsContent value="customization">
                  <CustomizationSection form={form} />
                </PageTabsContent>
                <PageTabsContent value="analytics">
                  <AnalyticsSection form={form} />
                </PageTabsContent>
              </form>
            </PageTabs>
          </div>
        </DialogHeader>
        <DialogFooter className="bg-muted rounded-b-md">
          <Button variant="outline" onClick={onCancel} data-testid="booking-page-form-cancel">
            {t("Cancel")}
          </Button>
          <Button
            onClick={() => form.submitForm()}
            className="font-light"
            disabled={bookingPage ? !formIsDirty || !form.isValid : !form.isValid}
            loading={updateBookingPageMutation.isPending || createBookingPageMutation.isPending}
            data-testid="booking-page-form-submit"
          >
            {bookingPage && !shouldDuplicate ? t("Save Changes") : t("Create Booking Page")}
          </Button>
        </DialogFooter>
      </DialogContent>
    );
  },
  ({ bookingPage, bookingPageId }) => bookingPage?.id ?? bookingPageId ?? ""
);
